import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useInactivityLogout } from "../hooks/useInactivityLogout";
import { PORTAL_BOOT_PENDING_KEY } from "../lib/portalBootSession";
import { clearSessionToken, setSessionToken } from "../lib/sessionToken";

const AuthContext = createContext(null);

function readPersistedActiveClient() {
  try {
    const saved = localStorage.getItem("taistu_active_client");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

/** Clear legacy JWT storage — durable cookie + tab sessionStorage hold the session. */
function clearLegacyToken() {
  try {
    localStorage.removeItem("taistu_token");
  } catch {
    // ignore
  }
}

function clearAllClientTokens() {
  clearSessionToken();
  clearLegacyToken();
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null); // current client context (admin can switch)
  const [loading, setLoading] = useState(true);
  /** True after `login()` until portal boot overlay finishes (mirrors sessionStorage for reliability). */
  const [portalBootPending, setPortalBootPending] = useState(false);
  const fetchGenRef = useRef(0);

  const fetchMe = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    try {
      const { data } = await api.get("/auth/me");
      if (gen !== fetchGenRef.current) return false;
      clearLegacyToken();
      setUser(data.user);
      if (data.user?.role === "client" || data.client) {
        setClient(data.client);
      } else if (data.user?.role === "admin") {
        setClient(readPersistedActiveClient());
      } else {
        setClient(null);
      }
      return true;
    } catch (e) {
      // Ignore stale failures so an in-flight pre-login /auth/me cannot wipe a fresh session.
      if (gen !== fetchGenRef.current) return false;
      clearAllClientTokens();
      setUser(null);
      setClient(null);
      return false;
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = (token, userObj, clientObj, { boot = true } = {}) => {
    // Keep JWT in memory + sessionStorage so Authorization works even when the
    // httpOnly cookie is delayed or blocked (cross-site). Cookie remains preferred.
    clearLegacyToken();
    if (token) setSessionToken(token);
    setUser(userObj);
    if (clientObj) setClient(clientObj);
    if (!boot) {
      setPortalBootPending(false);
      try {
        sessionStorage.removeItem(PORTAL_BOOT_PENDING_KEY);
      } catch {}
      return;
    }
    setPortalBootPending(true);
    try {
      sessionStorage.setItem(PORTAL_BOOT_PENDING_KEY, "1");
    } catch {}
  };

  const acknowledgePortalBoot = useCallback(() => {
    setPortalBootPending(false);
    try {
      sessionStorage.removeItem(PORTAL_BOOT_PENDING_KEY);
    } catch {}
  }, []);

  const logout = useCallback(() => {
    setPortalBootPending(false);
    try {
      sessionStorage.removeItem(PORTAL_BOOT_PENDING_KEY);
    } catch {}
    api.post("/auth/logout").catch(() => {});
    clearAllClientTokens();
    localStorage.removeItem("taistu_active_client");
    setUser(null);
    setClient(null);
    window.location.href = "/";
  }, []);

  const switchClient = (c) => {
    setClient(c);
    if (c) localStorage.setItem("taistu_active_client", JSON.stringify(c));
    else localStorage.removeItem("taistu_active_client");
  };

  useInactivityLogout(Boolean(user), logout);

  return (
    <AuthContext.Provider
      value={{
        user,
        client,
        loading,
        login,
        logout,
        switchClient,
        refresh: fetchMe,
        portalBootPending,
        acknowledgePortalBoot,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
